import TvCarouselContainer from '../components/TvCarouselContainer/TvCarouselContainer'
import { TvBadgeStyleEnum, TvCardStylesEnum, TvCardTypesEnum } from '../types/flo-enums'
import {
  CardItemModel,
  ImageModel,
  LinkModel,
  PlayerModel,
  SportData,
  StreamItem,
  TextModel,
  TvBadgeModel,
  TvBrowseLayoutModel,
  TvCarouselContainerModel,
  TvEventCardModel,
  TvSpotlightModel,
} from '../types/flo-experience'
import { isUserSubscriptionActive } from './authentication'
import { getText } from './dictionary'
import { joinStrings } from './misc'

export const getTopBadge = (statusV2: string) => {
  switch (statusV2) {
    case 'ADS-PRE':
      return toBadgeModel(TvBadgeStyleEnum.Alternate, 'STARTING SOON')
    case 'CONCLUDED':
    case 'ADS-POST':
      return toBadgeModel(TvBadgeStyleEnum.Standard, 'COMPLETED')
    case 'LIVE':
      return toBadgeModel(TvBadgeStyleEnum.Live, 'LIVE')
    case 'INTERMISSION':
      return toBadgeModel(TvBadgeStyleEnum.Standard, 'INTERMISSION')
    case 'UNKNOWN':
    case 'PRE-AIR':
    default:
      return null
  }
}

export const toTextModel = (text: string) => {
  return {
    type: 'raw-text',
    style: 'standard',
    text: text,
  } as TextModel
}

export const toBadgeModel = (style: string, text = '', icon?: string) => {
  return {
    ...(icon && {
      icon: {
        style: icon,
        type: 'icon',
      },
    }),
    style: style,
    type: 'tv-badge',
    ...(text && {
      text: toTextModel(text),
    }),
  } as TvBadgeModel
}

export const toImageModel = (url: string) => {
  return {
    type: 'image',
    style: 'standard',
    url: url,
  } as ImageModel
}

export const toSpotlightModel = (spotlight, previewVideoAnalytics, statusBadges) => {
  return {
    ...spotlight,
    status_badges: statusBadges,
    background_preview_video_analytics_metadata: previewVideoAnalytics,
  } as TvSpotlightModel
}

export const toAction = (stream, fullVideoAnalytics) => {
  return {
    content_metadata: {
      stream_id: stream?.id ?? 0,
      stream_name: stream?.name ?? '',
      is_premium: stream?.isPremium ?? false,
    },
    analytics: {
      entity_id: stream?.id ?? 0,
      entity_type: 'event',
      video_analytics: fullVideoAnalytics,
    },
  } as LinkModel
}

export const generateBFFCarousel = () => {
  return {
    id: 'tv-event-hub-carousel-streams',
    type: 'container:tv-carousel',
    style: 'standard',
    axis: 'horizontal',
    distribution: 'start',
    title: toTextModel(getText('eventStreams')),
    items: [],
  } as TvCarouselContainerModel
}

export const updatedStreamCard = (
  status = '',
  activeStreams = [],
  existingCardIndex = 0,
  latestStream,
  sportsDataArr = [],
) => {
  const stream = activeStreams[existingCardIndex] as CardItemModel

  const topBadge = getTopBadge(status)

  const sportsData = sportsDataArr.find(
    (data: any) => data.identity.liveStreamId === stream.action.content_metadata.stream_id,
  )

  if (sportsData) {
    return {
      ...stream,
      sports_data: sportsData,
    } as CardItemModel
  } else {
    return {
      ...stream,
      leading_top_badges: topBadge ? [topBadge] : null,
      title: toTextModel(stream?.title?.text ?? latestStream?.name),
    } as CardItemModel
  }
}

export const createSpeechTextForCards = (data, actionString, cardIndex = 0, totalCards = 0) => {
  let text = ''

  if (totalCards > 0) {
    text += cardIndex + 1 + ' of ' + totalCards + ' items . '
  }

  if (data?.title) {
    text += data?.title?.text + ' . . '
  }

  if (data?.details) {
    text += data?.details?.text + ' . . '
  }

  if (data?.footer) {
    text += data?.footer?.text + ' . . '
  }

  if (data?.leading_top_badges && data?.leading_top_badges?.length != 0) {
    const icon =
      data?.leading_top_badges?.[0]?.text?.text ??
      getIconName(data?.leading_top_badges?.[0]?.icon?.style)

    text += icon + ' . '
  }

  if (data?.leading_bottom_badges && data?.leading_bottom_badges?.length != 0) {
    const icon =
      data?.leading_bottom_badges?.[0]?.text?.text ??
      getIconName(data?.leading_bottom_badges?.[0]?.icon?.style)

    text += icon + ' . '
  }

  if (data?.trailing_top_badges && data?.trailing_top_badges?.length != 0) {
    let icon =
      data?.trailing_top_badges?.[0]?.text?.text ??
      getIconName(data?.trailing_top_badges?.[0]?.icon?.style)

    if (icon === 'minus') {
      icon = ''
      actionString = 'Select to remove from favorites'
    } else if (icon === 'plus') {
      icon = ''
      actionString = 'Select to add to favorites'
    } else if (icon === 'lock') {
      icon = 'locked'
    } else if (icon === 'star-filled') {
      icon = 'favorited'
    }

    text += icon + ' . '
  }

  if (data?.trailing_bottom_badges && data?.trailing_bottom_badges?.length != 0) {
    const icon =
      data?.trailing_bottom_badges?.[0]?.text?.text ??
      getIconName(data?.trailing_bottom_badges?.[0]?.icon?.style)

    text += icon + ' . '
  }

  text += actionString

  return text
}

export const getIconName = (name: string): string => {
  return name.replace(/^icon-/, '')
}

export const liveStreamToCard = (stream, status, data) => {
  const eventName = data?.spotlight?.title?.text ?? ''

  const isSubscriptionActive = isUserSubscriptionActive()
  let trailingTopBadges = null
  let spotlightMetadata = null

  const previewVideoAnalytics = {
    event_name: joinStrings(' | ', eventName, stream?.name) ?? '',
    stream_id: stream?.id,
  }
  const fullVideoAnalytics = {
    ...data?.spotlight?.background_preview_video_analytics_metadata,
    ...data?.analytics?.video_analytics,
    ...previewVideoAnalytics,
  }

  const leadingTopBadges = getTopBadge(status)

  if (!isSubscriptionActive) {
    trailingTopBadges = [toBadgeModel(TvBadgeStyleEnum.LightShadow, '', 'icon-lock')]
  }

  const streamMetadata = stream

  if (data?.spotlight) {
    spotlightMetadata = toSpotlightModel(data?.spotlight, previewVideoAnalytics, leadingTopBadges)
  }

  const card = {
    id: `${TvCardTypesEnum.TvEventCard}-${stream?.id}`,
    type: TvCardTypesEnum.TvEventCard,
    style: TvCardStylesEnum.Large,
    leading_top_badges: leadingTopBadges ? [leadingTopBadges] : null,
    trailing_top_badges: trailingTopBadges,
    details: toTextModel(streamMetadata?.name),
    preview_image: toImageModel(streamMetadata?.streamThumbnailUrl),
    title: toTextModel(fullVideoAnalytics?.event_name),
    ...(spotlightMetadata && {
      spotlight_metadata: spotlightMetadata,
    }),

    action: toAction(stream, fullVideoAnalytics),
  } as TvEventCardModel

  return card
}

export const getUpdatedCardItems = (
  sportsData: SportData[],
  latestStreams: StreamItem[] = [],
  status: string,
  pageData: TvBrowseLayoutModel | PlayerModel,
  streamsCarousel?: TvCarouselContainer,
) => {
  // Find which streams are already on the Event Hub in relation to the ones coming from Firebase
  const filteredActiveStreams = streamsCarousel
    ? streamsCarousel?.items?.filter((active: CardItemModel) =>
        latestStreams.some((stream) => stream.id === active?.action?.content_metadata?.stream_id),
      )
    : []

  const updatedStreams: any[] = []

  latestStreams.forEach((latestStream) => {
    // Check if stream already exists
    const existingCardIndex = filteredActiveStreams.findIndex(
      (active: CardItemModel) => active?.action?.content_metadata?.stream_id === latestStream.id,
    )

    // If it doesn't exist create the card item with the same BFF structure
    if (existingCardIndex < 0) {
      updatedStreams.push(liveStreamToCard(latestStream, status, pageData))
    } else {
      // If stream exists already just update the card
      updatedStreams.push(
        updatedStreamCard(
          status,
          filteredActiveStreams,
          existingCardIndex,
          latestStream,
          sportsData,
        ),
      )
    }
  })

  return updatedStreams
}

export const buildPlaylistFromGrid = (grid, isSearchGrid = false) => {
  const limit = 20
  const data = grid?.data
  const items = grid?.items
  const selectedIndex = grid?.selectedIndex

  const carouselItems = items
    ?.slice(selectedIndex + 1, limit + 1)
    ?.map((item) => ({ ...item, style: 'large' }))

  return {
    id: isSearchGrid ? data?.content?.id : data?.id,
    type: 'container:tv-carousel',
    style: 'standard',
    axis: 'horizontal',
    items: carouselItems,
    distribution: 'start',
  }
}

export const clearLockIcon = (cardData: CardItemModel) => {
  if (isUserSubscriptionActive()) {
    if (Array.isArray(cardData.trailing_top_badges)) {
      cardData.trailing_top_badges = cardData.trailing_top_badges.filter((badge) => {
        return !(badge.icon && badge.icon.style === 'icon-lock')
      })
    }
  }
}
